import {
  FireFilled,
  LeftOutlined,
  PlusOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { Button, Flex, Motion, Text } from "../../components";
import { useApp, useHorizontalScroll, useMenu } from "../../hooks";
import { useCartStore } from "../../stores";
import { Item } from "../../types/item";
import { previewLineTotal } from "../../utils/menu";
import { Tools } from "../../utils";

const priceOf = (item: Item) =>
  item.hasVariations
    ? previewLineTotal({ ...item, price: 0 }, 1) || item.totalPrice
    : item.price;

const Suggestions = () => {
  const cartStore = useCartStore();
  const { isSiteActive, message } = useApp();
  const { cartSuggestions: items, openItem } = useMenu();

  const onAdd = (item: Item) => {
    if (!(item.isAvailable ?? true) || !isSiteActive) {
      message.error(
        !isSiteActive
          ? "We are temporarily closed and not accepting orders."
          : `${item.name} is currently unavailable`,
      );
      return;
    }
    if (item.options?.length) {
      openItem(item.uuid);
      return;
    }
    cartStore.add(item).catch((error: any) => {
      message.error(error.message || "Could not update cart");
    });
  };

  const inCart = new Set(cartStore.items.map((line) => line.uuid));
  const visible = items.filter(
    (item) => (item.isAvailable ?? true) && !inCart.has(item.uuid),
  );
  const {
    ref: scrollerRef,
    needsScroll,
    canScrollLeft,
    canScrollRight,
    scroll,
  } = useHorizontalScroll(visible.map((item) => item.uuid).join(","));

  if (!visible.length) return null;

  const scrollButton = (direction: -1 | 1, enabled: boolean) => {
    const isLeft = direction < 0;
    return (
      <Button
        disabled={!enabled}
        aria-disabled={!enabled}
        className={`!rounded-full`}
        onClick={() => enabled && scroll(direction)}
        aria-label={`Scroll suggestions ${isLeft ? "left" : "right"}`}
        icon={isLeft ? <LeftOutlined /> : <RightOutlined />}
      />
    );
  };

  return (
    <div className="-mx-2">
      <Flex align="center" justify="space-between" className="mb-[15px]">
        <Flex align="center" gap="0.6rem">
          <Motion name="attention" className="text-[1.8rem] text-primary">
            <FireFilled />
          </Motion>
          <Text level={5} children="Popular with your order" />
        </Flex>
        {needsScroll && (
          <Flex align="center" gap="1rem" className="shrink-0">
            {scrollButton(-1, canScrollLeft)}
            {scrollButton(1, canScrollRight)}
          </Flex>
        )}
      </Flex>
      <div
        ref={scrollerRef}
        className="flex items-start gap-[1rem] overflow-x-auto px-2 pb-[0.4rem] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {visible.map((item) => {
          const price = priceOf(item);
          return (
            <button
              key={item.uuid}
              type="button"
              aria-label={`Add ${item.name}`}
              onClick={() => onAdd(item)}
              className="w-[10rem] shrink-0 border-0 bg-transparent p-0 text-left cursor-pointer"
            >
              <div className="relative h-[10rem] overflow-hidden rounded-[1rem] bg-primary-soft">
                {!!item.imageUrl && (
                  <img
                    alt=""
                    src={Tools.mediaUrl(item.imageUrl)}
                    className="h-full w-full object-contain"
                  />
                )}
                <PlusOutlined className="absolute bottom-[0.4rem] right-[0.4rem] h-[2.8rem] w-[2.8rem] justify-center rounded-full bg-primary text-[1.3rem] text-on-primary shadow-card" />
              </div>
              <Flex vertical className="mt-[15px]">
                <Text
                  level="text"
                  weight={700}
                  children={Tools.toCurrency(price)}
                />
                <Text
                  weight={400}
                  size={10}
                  type="secondary"
                  children={item.name}
                />
              </Flex>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default Suggestions;
